/* External */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

/* Utils */
import {
  useAreYouStillWatchingInactiveTime,
  useAreYouStillWatchingEpisodeLimit,
} from '../../../../providers/ConfigProvider';
import useRefSync from '../../../../hooks/useRefSync';

export default function useAreYouStillWatching({
  isReadyToPlay,
  isLive,
  isDelayed,
  isPlaying,
  counterEpisode,
  lastUserInteraction,
  handleUserInteraction,
  play,
  pause,
  goToLive,
}) {
  const [isStillWatchingActive, setIsStillWatchingActive] = useState(false);
  const isStillWatchingActiveRef = useRefSync(isStillWatchingActive);
  const isDelayedRef = useRefSync(isDelayed);
  const isPlayingRef = useRefSync(isPlaying);
  const delayedWhenDisplay = useRef(false);

  const episodeLimit = useAreYouStillWatchingEpisodeLimit();
  const inactiveTime = useAreYouStillWatchingInactiveTime(isLive);

  const showAreYouStillWatching = useCallback(() => {
    if (!isStillWatchingActiveRef.current && isPlayingRef.current) {
      delayedWhenDisplay.current = isDelayedRef.current;
      pause();
      setIsStillWatchingActive(true);
    }
  }, [
    isStillWatchingActiveRef,
    isDelayedRef,
    isPlayingRef,
    pause,
  ]);

  const handleContinueWatching = useCallback(() => {
    handleUserInteraction();
    setIsStillWatchingActive(false);
    if (isLive && !delayedWhenDisplay.current) {
      goToLive();
    } else {
      play();
    }
  }, [handleUserInteraction, isLive, goToLive, play]);

  useEffect(() => {
    let timeout;
    if (isReadyToPlay && (isLive || counterEpisode > episodeLimit)) {
      timeout = setTimeout(showAreYouStillWatching, inactiveTime);
    }
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [
    isReadyToPlay,
    lastUserInteraction,
    isLive,
    counterEpisode,
    episodeLimit,
    showAreYouStillWatching,
    inactiveTime,
  ]);

  return [
    isStillWatchingActive,
    handleContinueWatching,
  ];
}
